import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";
import { useEnquiry } from "../enquiry-context";
import { scrollToSection } from "../components/Header";
import { EASE } from "../components/Reveal";

const HERO_VIDEO = "/videos/hero-foundry.mp4";
const HERO_POSTER = "/img/furnace.jpg";

function MaskedLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
      <motion.span
        className={`block ${className || ""}`}
        initial={{ y: "112%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.05, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero() {
  const { openEnquiry } = useEnquiry();
  const videoRef = useRef(null);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = true;
    const tryPlay = () => {
      v.play().catch(() => {});
    };
    tryPlay();
    v.addEventListener("loadeddata", tryPlay);
    v.addEventListener("canplay", tryPlay);
    return () => {
      v.removeEventListener("loadeddata", tryPlay);
      v.removeEventListener("canplay", tryPlay);
    };
  }, []);

  return (
    <section id="top" className="relative min-h-screen overflow-hidden bg-[#080C14]">
      {/* cinematic foundry video */}
      <div className="absolute inset-0" data-testid="hero-video-wrapper">
        <video
          ref={videoRef}
          data-testid="hero-video"
          className="h-full w-full object-cover slow-zoom"
          src={HERO_VIDEO}
          poster={HERO_POSTER}
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#080C14]/95 via-[#080C14]/70 to-[#080C14]/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#080C14]/90 via-[#080C14]/10 to-[#080C14]/50" />
      </div>

      <div className="relative z-10 mx-auto flex min-h-screen w-full max-w-7xl flex-col justify-center px-5 sm:px-8 pt-24 pb-20">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="eyebrow flex items-center gap-3"
          data-testid="hero-eyebrow"
        >
          <span className="inline-block h-px w-10 bg-amber-400/80" />
          SamayPur, Delhi — India
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="mt-5 font-display font-bold uppercase leading-[0.88] tracking-tight text-[clamp(3.4rem,9.5vw,7.6rem)]"
        >
          <MaskedLine delay={0.25} className="text-stone-50">Aluminium</MaskedLine>
          <MaskedLine delay={0.37} className="text-outline-light">Pressure Die</MaskedLine>
          <MaskedLine delay={0.49} className="text-molten">Casting.</MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subheading"
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-6 max-w-xl text-base sm:text-lg leading-relaxed text-stone-300"
        >
          Precision cast components for industrial, automotive, electrical and engineering
          applications — developed from your drawing or sample through to final supply.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-8 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-products"
            onClick={() => scrollToSection("#products")}
            className="inline-flex items-center gap-2 rounded-full bg-amber-500 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-stone-950 hover:bg-amber-400 hover:shadow-[0_14px_36px_rgba(217,160,43,0.45)] transition-all"
          >
            Explore Products <ArrowUpRight size={16} strokeWidth={2.5} />
          </button>
          <button
            data-testid="hero-cta-enquiry"
            onClick={() => openEnquiry("General Enquiry")}
            className="inline-flex items-center gap-2 rounded-full border border-white/40 px-7 py-3.5 text-sm font-bold uppercase tracking-widest text-white hover:bg-white hover:text-stone-950 transition-all"
          >
            Send Enquiry
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-12 grid max-w-xl grid-cols-3 gap-4 border-t border-white/15 pt-6"
        >
          {[
            ["50 g – 800 g", "Component range"],
            ["10-step", "Integrated process"],
            ["Drawing to dispatch", "One roof"],
          ].map(([big, small]) => (
            <div key={small}>
              <p className="font-display text-xl sm:text-2xl font-bold uppercase text-stone-50">{big}</p>
              <p className="mt-1 text-[11px] font-tech uppercase tracking-[0.18em] text-stone-400">{small}</p>
            </div>
          ))}
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToSection("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2 text-white/60 hover:text-amber-400 transition-colors"
        aria-label="Scroll to about section"
        data-testid="hero-scroll-hint"
      >
        <span className="font-tech text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <ArrowDown size={16} className="animate-bounce" />
      </motion.button>
    </section>
  );
}
